import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Prescription } from '@/types/prescription';
import { getPrescriptionById } from '@/lib/api';
import EditPrescriptionModal from '@/components/Prescription/EditPrescriptionModal';

// Fetch prescription by ID from the API
const fetchPrescription = async (prescriptionId: string): Promise<Prescription | null> => {
  try {
    const response = await getPrescriptionById(prescriptionId);
    return response;
  } catch (error) {
    console.error('Failed to fetch prescription:', error);
    return null;
  }
};

export default function EditPrescriptionPage() {
  const { id: prescriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPrescription = async () => {
      if (!prescriptionId) {
        setError('No prescription ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const prescriptionData = await fetchPrescription(prescriptionId);
        
        if (!prescriptionData) {
          setError('Prescription not found');
        } else {
          setPrescription(prescriptionData);
        }
      } catch (err) {
        console.error('Error loading prescription:', err);
        setError('Failed to load prescription');
      } finally {
        setLoading(false);
      }
    };

    loadPrescription();
  }, [prescriptionId]);

  const handlePrescriptionUpdated = () => {
    toast({
      title: "Success",
      description: "Prescription updated successfully",
      variant: "default"
    });
    
    // Navigate back to prescriptions list or patient page
    navigate('/prescriptions');
  };

  const handleClose = () => {
    navigate('/prescriptions');
  };

  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescription...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Error Loading Prescription</h3>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => window.location.reload()} variant="outline">
                  Try Again
                </Button>
                <Button onClick={handleClose}>
                  Go Back
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show placeholder until prescription is loaded
  if (!prescription) {
    return (
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Prescription Not Found</h3>
              <p className="text-muted-foreground mb-6">
                The prescription you're looking for doesn't exist or couldn't be loaded.
              </p>
              <Button onClick={handleClose}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
    );
  }

  // Render the edit modal as a full-page component
  return (
    <div className="prescription-form-container">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleClose}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Edit Prescription</h1>
              <p className="text-muted-foreground">
                Prescription ID: {prescriptionId}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal - Always open when component is rendered */}
      <EditPrescriptionModal
        open={true}
        prescription={prescription}
        onClose={handleClose}
        onUpdated={handlePrescriptionUpdated}
      />
    </div>
  );
} 